import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { ProcessorFeeStatusLine } from '@signalvote/shared';
import { FEE_ROW_STATE_LABELS, ProcessorFeeStepAsideReason } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatAmount, fmtDateShort } from '@/components/finance/utils';
import { cn } from '@/lib/utils';
import { useBookProcessorFee } from '@/hooks/queries/useBookProcessorFee';
import { useStepAsideProcessorFee } from '@/hooks/queries/useStepAsideProcessorFee';
import { useProcessorFeeDonations } from '@/hooks/queries/useProcessorFeeDonations';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { LEDGER_ROW_GRID_TEMPLATE } from './TriageLedger';
import { answered } from '@/lib/reportsRead';
import { skip } from '@signalvote/data';

/**
 * The calculated processor fee as a PINNED REGISTER ROW inside the close-hub ledger, sitting
 * under the in-period (owed) header — the period being filed (issue #108). One synthetic row per
 * fee-bearing processor that still OWES a fee.
 *
 * The pin is BINARY and always actionable. Whenever the owed period still owes a fee (armed —
 * calculated minus already-recorded is past tolerance) it shows a primary **Record** plus a
 * secondary **"I'll handle it myself"** step-aside (issue #110); once the fee is recorded, stepped
 * aside, or there is nothing to owe the pin shows NOTHING. There is no view-only / "heads-up" row,
 * no dead "Record now" link, and no in-ledger undo — a treasurer is never shown a fee row they
 * can't act on. Booking is PERIOD-SCOPED: {@link useBookProcessorFee} / {@link useStepAsideProcessorFee}
 * send only the processor identity (plus, for step-aside, the reason + optional note); the server
 * resolves the owed period, so both actions are live from the start of the period, not only at close.
 *
 * Structurally WEIGHTLESS: it is NOT a sort-inbox item and never enters `sortInbox.totalInPeriod`,
 * the triage/ready counts, cash-on-hand, selection/checkboxes, or the file gate. The ledger pins
 * it as a `header`-type RenderRow so no count/total/selection derivation can see it.
 *
 * Non-color signals (dashed frame + hollow dashed-ring marker + the "Calculated" label + a
 * definite amount) carry the "not-yet-recorded" meaning so it never reads as a recorded entry,
 * even in grayscale.
 */

interface ProcessorFeeLedgerRowsProps {
  processorFeeLines: ProcessorFeeStatusLine[];
}

/**
 * A fee line carries a display-only forecast ONLY — the muted "expected total" heading — when it
 * is not armed (nothing confirmed yet to record), nothing has been booked, and a positive forecast
 * exists (a fresh, all-Pending processor import). Once the treasurer confirms a gift the line arms
 * (a Record pin); once a fee is booked it drops off; so this is the pre-confirm state alone.
 */
function isForecastOnlyLine(line: ProcessorFeeStatusLine): boolean {
  return !line.isNudgeArmed && (line.bookedFeeTotal ?? 0) === 0 && line.forecast > 0;
}

/**
 * Whether a fee line is worth pinning into the ledger — an armed Record pin OR a muted forecast
 * heading (ADR-045). TriageLedger's `hasPinnedFee` mirrors this so it never emits an empty header
 * row (which would still occupy a virtualizer index) nor drops a row this component would render.
 */
export function isFeeLinePinnable(line: ProcessorFeeStatusLine): boolean {
  return line.isNudgeArmed || isForecastOnlyLine(line);
}

/**
 * Renders the pinned fee rows as bare siblings (no wrapping band) so the ledger can inject them
 * directly beneath the in-period (owed) header. Two shapes (ADR-045): an ARMED processor renders a
 * live amber Record pin (the recordable amount still owed); a fresh-import processor with only a
 * forecast renders a MUTED, non-actionable "expected total" heading — never a Record target, so the
 * fee is visible on import without asking the treasurer to book a fee on money they haven't
 * reviewed. A recorded / settled / stepped-aside processor shows nothing. Returns `null` when no
 * processor is pinnable, so the ledger pins nothing.
 */
export function ProcessorFeeLedgerRows({
  processorFeeLines,
}: ProcessorFeeLedgerRowsProps) {
  const { mutate: bookFee, isPending: bookPending } = useBookProcessorFee();
  const { mutate: stepAside, isPending: stepAsidePending } = useStepAsideProcessorFee();
  const { isPortalUpload } = usePortalUploadModel();

  if (!processorFeeLines.some(isFeeLinePinnable)) return null;

  // Jurisdiction-aware "add it later" copy, resolved at this render boundary (the enum value is
  // jurisdiction-neutral). A portal-upload filer (Alabama today) has no amendment pre-filing — a
  // recorded fee just rides the next incremental upload (ADR-020) — so the copy says "on a later
  // upload", NOT "by amending"; a federal in-system e-filer amends the filed report. Same
  // filing-model source every other finance surface reads (usePortalUploadModel) — never a
  // re-hardcoded `stateCode === 'AL'`.
  const addLaterLabel = isPortalUpload
    ? "I'll add it on a later upload"
    : "I'll add it later by amending";

  return (
    <>
      {processorFeeLines.map((line) => {
        // Armed → the actionable Record pin (recordable amount owed).
        if (line.isNudgeArmed) {
          return (
            <ProcessorFeeLedgerRow
              key={line.processorId}
              line={line}
              isPending={bookPending || stepAsidePending}
              addLaterLabel={addLaterLabel}
              onRecord={() => bookFee({ processorId: line.processorId })}
              onStepAside={(reason, note) =>
                stepAside({ processorId: line.processorId, reason, note: note ?? null })
              }
            />
          );
        }
        // Pre-confirm forecast → the muted, non-actionable "expected total" heading.
        if (isForecastOnlyLine(line)) {
          return <ProcessorFeeForecastRow key={line.processorId} line={line} />;
        }
        // Recorded / settled / stepped-aside / no fee → not a row (binary).
        return null;
      })}
    </>
  );
}

/**
 * The display-only [[Fee forecast]] "expected total" heading (ADR-045). Shown for a fresh,
 * all-Pending processor import so the fee is never invisible-then-fully-formed. Deliberately
 * DISTINCT from the amber Record pin: muted slate, no Record button, no step-aside — the treasurer
 * is never asked to book a fee on money they haven't reviewed. It reads as a heading ("Expected …",
 * "expected this period"), not a bill due now; it converges into the Record pin as gifts are
 * confirmed.
 */
function ProcessorFeeForecastRow({ line }: { line: ProcessorFeeStatusLine }) {
  return (
    <div
      data-testid={`processor-fee-forecast-${line.processorId}`}
      data-slot="processor-fee-forecast"
      // Same grid template as a ledger data row so cells sit under the Date / Payee /
      // Category / Amount headers.
      style={{ gridTemplateColumns: LEDGER_ROW_GRID_TEMPLATE }}
      className={cn(
        'grid items-center gap-x-2 rounded-md border border-dashed p-2 text-sm',
        // Muted slate — distinctly NOT the amber armed pin. A heading, not a bill to pay now; the
        // dashed frame keeps the "not recorded" meaning without the owed-amber urgency.
        'border-slate-300 bg-slate-50 text-slate-600',
      )}
    >
      {/* Col 1 (checkbox gutter): a muted hollow marker — never a filled/checked marker. */}
      <span
        data-testid="fee-row-forecast-marker"
        aria-hidden="true"
        className="h-4 w-4 shrink-0 rounded-full border border-dashed border-current opacity-60"
      />
      {/* Col 2 (Date): "this period so far", not a dated transaction. */}
      <span className="whitespace-nowrap font-mono text-xs opacity-70">—</span>
      {/* Col 4 (Payee): processor name + an "Expected" heading label, so it never reads as a
          calculated amount owed. */}
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="truncate">{line.processorName} · Expected processor fee</span>
        <span
          data-testid="fee-row-forecast-label"
          className="rounded-full border border-current px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
        >
          Expected
        </span>
      </span>
      {/* Col 5 (Category / actions): intentionally EMPTY — the forecast is NEVER actionable (no
          Record, no step-aside). */}
      <span aria-hidden />
      {/* Col 6 (Amount): the forecast total, captioned so it reads as a heading, not a bill. A
          definite figure, no tilde. */}
      <span className="flex flex-col items-end whitespace-nowrap text-right font-mono text-sm">
        <span className="tabular-nums">{formatAmount(line.forecast)}</span>
        <span className="whitespace-nowrap text-[10px] font-normal opacity-70">
          expected this period
        </span>
      </span>
      {/* Col 7 (action gutter): empty. */}
      <span aria-hidden />
    </div>
  );
}

function ProcessorFeeLedgerRow({
  line,
  isPending,
  addLaterLabel,
  onRecord,
  onStepAside,
}: {
  line: ProcessorFeeStatusLine;
  isPending: boolean;
  addLaterLabel: string;
  onRecord: () => void;
  onStepAside: (reason: ProcessorFeeStepAsideReason, note?: string) => void;
}) {
  // Book-the-difference: the pinned amount is the remainder still owed (estimate − already
  // recorded). Once ANY fee has been recorded for the period and new donations re-arm the pin
  // (issue #112), a bare remainder reads ambiguously — is it incremental, or the whole fee again?
  // So when something has been recorded, reconcile it out loud (recorded X of Y estimated) and
  // label the amount a remainder, so a re-armed pin reads as a top-up, never a fresh total to
  // double-book. A first pin (nothing recorded) stays one clean number.
  const recorded = line.bookedFeeTotal ?? 0;
  const hasRecorded = recorded > 0;
  return (
    <div
      data-testid={`processor-fee-line-${line.processorId}`}
      data-slot="processor-fee-line"
      // The SAME grid template as a ledger data row so the cells sit under the
      // Date / Payee / Category / Amount headers. `p-2` mirrors the ledger
      // summary line's per-cell padding.
      style={{ gridTemplateColumns: LEDGER_ROW_GRID_TEMPLATE }}
      className={cn(
        'grid items-center gap-x-2 rounded-md border border-dashed p-2 text-sm',
        // One owed state: a full-tint amber dashed row (no hue swap, no side-stripe border).
        'border-amber-400 bg-amber-100/70 text-amber-900',
      )}
    >
      {/* Col 1 (checkbox gutter): the hollow dashed-ring marker — never a filled
          or checked marker, so a not-yet-recorded row never reads as recorded. */}
      <span
        data-testid="fee-row-est-marker"
        aria-hidden="true"
        className="h-4 w-4 shrink-0 rounded-full border border-dashed border-current"
      />
      {/* Col 2 (Date): the pinned row is "this period so far", not a dated transaction. */}
      <span className="whitespace-nowrap font-mono text-xs opacity-80">—</span>
      {/* Col 4 (Payee / contributor): processor name + the "Calculated" state label
          from the shared treasurer-first map. Once part of the fee has been recorded,
          a muted reconciling sub-line ("recorded X of Y estimated") sits beneath, so a
          re-armed remainder is obviously a top-up on a partly-paid total. */}
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="truncate">{line.processorName} · Calculated processor fee</span>
          <span
            data-testid="fee-row-state-label"
            className="rounded-full border border-current px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wide"
          >
            {FEE_ROW_STATE_LABELS[line.state]}
          </span>
        </span>
        {hasRecorded && (
          <span
            data-testid="fee-row-recorded-breakdown"
            className="text-xs font-normal opacity-70"
          >
            {formatAmount(recorded)} recorded of {formatAmount(line.estimatedFee)} estimated
          </span>
        )}
        {/* Issue #247 — ActBlue returns its OWN platform fee on a refund (only the card fee is
            kept), so a refunded ActBlue gift makes this estimate read slightly high. A qualitative
            heads-up ONLY (the overstatement isn't computable from ActBlue's blended rate); the
            popover explains what to do about it. */}
        {line.showActBlueRefundCaveat && (
          <span
            data-testid="fee-row-actblue-refund-caveat"
            className="text-xs font-normal opacity-80"
          >
            Includes a refunded ActBlue gift — ActBlue returns its own fee on refunds, so this may
            read slightly high.
          </span>
        )}
        {/* "Show your work" (issue #116): expand to see the period's donations behind this
            estimate. A secondary, low-emphasis affordance — the reconciling line above is the
            primary fix for increment-vs-total confusion; this is the detail view. */}
        <FeeDonationsPopover
          processorId={line.processorId}
          processorName={line.processorName}
        />
      </span>
      {/* Col 5 (Category): the calculated fee carries no purpose code, so this
          otherwise-blank cell hosts the actions — right-aligned so they sit just
          LEFT of the amount with a clear gap. The secondary step-aside sits beside
          the primary Record; both wrap under font scaling rather than overflowing. */}
      <span className="flex flex-wrap items-center justify-end gap-1 text-right">
        <StepAsidePicker
          addLaterLabel={addLaterLabel}
          isPending={isPending}
          onConfirm={onStepAside}
        />
        <Button
          data-testid="fee-row-record"
          type="button"
          size="sm"
          disabled={isPending}
          onClick={onRecord}
        >
          Record
        </Button>
      </span>
      {/* Col 6 (Amount): the calculated dollar figure still owed (the difference —
          rate × donations so far, less anything already recorded) ONLY, right-
          aligned under the ledger's Amount column. The caption reads "left to record"
          once part of the fee is booked (pairs with the col-4 reconciling line), else
          "to record this period". A definite figure, no tilde. */}
      <span className="flex flex-col items-end whitespace-nowrap text-right font-mono text-sm">
        <span className="tabular-nums">{formatAmount(line.difference)}</span>
        <span className="whitespace-nowrap text-[10px] font-normal opacity-70">
          {hasRecorded ? 'left to record' : 'to record'}
        </span>
      </span>
      {/* Col 7 (action gutter): intentionally EMPTY — the affordances live in col 5
          so a text button never overflows this 2rem gutter onto the amount. */}
      <span aria-hidden />
    </div>
  );
}

/**
 * The "show your work" donations popover (issue #116). Expanding a pinned fee row lists the
 * individual donations feeding this processor's estimate for the period — the SAME recordable set
 * the shown total sums over, so the header's count + total reconcile with the pin by construction.
 * It answers "where did this number come from?", NOT "which donations is this increment for?" — the
 * fee is <c>rate × Σ amount − recorded</c> with no per-donation attribution, so the copy names the
 * period's donations and never implies a per-increment subset (the already-shipped reconciling line
 * is the primary fix for that confusion; this is the secondary detail view).
 *
 * A portaled Popover — like the step-aside picker — so it never disturbs the virtualized ledger's
 * row heights. The list grows to fit its content up to a comfortable cap, then scrolls (never a
 * cramped box for a busy processor, never a full sheet for a small one). The donations query is
 * LAZY: enabled only while the popover is open, so an unexpanded row costs nothing.
 */
function FeeDonationsPopover({
  processorId,
  processorName,
}: {
  processorId: string;
  processorName: string;
}) {
  const [open, setOpen] = useState(false);
  const read = useProcessorFeeDonations(open ? processorId : skip);
  const data = answered(read);
  const isLoading = read.status === 'loading';
  const isError = read.status === 'failed' || read.status === 'offline';
  const donations = data?.donations ?? [];
  const count = donations.length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="fee-row-expand-donations"
          type="button"
          size="sm"
          variant="ghost"
          // Low-emphasis, legible against the amber pin — the reconciling line is the primary fix;
          // this is a detail affordance, so it stays quiet.
          className="h-auto w-fit gap-1 px-1 py-0.5 text-xs font-normal text-amber-900 hover:bg-amber-200/70"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
          />
          {open ? 'Hide donations' : 'Show donations'}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="fee-row-donations-panel"
        align="start"
        // Portal content bubbles clicks through the REACT tree, not the DOM tree, so a click inside
        // would otherwise reach a ledger row/card click handler (the same guard the step-aside
        // picker carries).
        onClick={(e) => e.stopPropagation()}
        // Grow to fit up to a comfortable cap, then scroll the list only.
        className="w-max min-w-[20rem] max-w-[92vw] p-0 text-left text-sm text-foreground"
      >
        <div className="space-y-1 border-b px-3 pb-2 pt-3">
          <p data-testid="fee-row-donations-header" className="font-medium">
            {isLoading
              ? 'Loading donations…'
              : `${count} ${count === 1 ? 'donation' : 'donations'} · ${formatAmount(
                  data?.donationTotal ?? 0,
                )}`}
          </p>
          <p
            data-testid="fee-row-donations-caption"
            className="text-xs font-normal text-muted-foreground"
          >
            The {processorName} donations this fee is calculated on this period — where this estimate
            comes from. The fee is based on the period&apos;s total, not tied to any single donation.
          </p>
        </div>
        <div className="max-h-[min(60vh,22rem)] overflow-y-auto px-3 pb-3">
          {isError ? (
            <p className="py-2 text-sm text-muted-foreground">
              Couldn&apos;t load the donations. Close this and try again.
            </p>
          ) : isLoading ? (
            <p className="py-2 text-sm text-muted-foreground">Loading…</p>
          ) : count === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">
              No donations feed this fee for the period yet.
            </p>
          ) : (
            <ul className="divide-y">
              {donations.map((d) => (
                <li
                  key={d.contributionId}
                  className="flex items-baseline justify-between gap-3 py-1.5"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate">{d.donorName || 'Unknown donor'}</span>
                    <span className="text-xs text-muted-foreground">
                      {fmtDateShort(d.date)}
                      {d.isRefunded && (
                        <span
                          data-testid={`fee-donation-refunded-${d.contributionId}`}
                          className="ml-1.5 rounded-full border border-current px-1 py-0.5 text-[10px] font-medium uppercase tracking-wide"
                        >
                          Refunded
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="shrink-0 font-mono tabular-nums">
                    {formatAmount(d.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {/* Issue #247 — the ActBlue refunded-gift heads-up. ActBlue returns its OWN platform fee on
            a refund (the card fee is kept), so a refunded ActBlue gift makes this estimate read a
            little high. Qualitative only (no computed figure) + points at a real existing lever
            ("I'll handle it myself"), never a made-up "lower the fee" control. */}
        {data?.showActBlueRefundCaveat && (
          <div
            data-testid="fee-row-donations-actblue-caveat"
            className="border-t px-3 py-2 text-xs text-muted-foreground"
          >
            ActBlue returns its own platform fee when a gift is refunded (the card fee is kept), so
            this estimate can read a little high. To book a different amount, choose &ldquo;I&apos;ll
            handle it myself&rdquo; and record the fee yourself.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The "I'll handle it myself" step-aside affordance (issue #110). A secondary, ghost-styled
 * trigger beside Record opens a compact reason picker: one of {entered-myself, no-fee-applies,
 * add-later} plus an optional note. Confirming calls the step-aside endpoint (the server resolves
 * the owed period); the pin then disappears with no lingering row and no undo. The picker is a
 * portaled Popover so it never disturbs the virtualized ledger's row heights.
 */
function StepAsidePicker({
  addLaterLabel,
  isPending,
  onConfirm,
}: {
  addLaterLabel: string;
  isPending: boolean;
  onConfirm: (reason: ProcessorFeeStepAsideReason, note?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<ProcessorFeeStepAsideReason | null>(null);
  const [note, setNote] = useState('');

  const reasons: { value: ProcessorFeeStepAsideReason; label: string }[] = [
    { value: ProcessorFeeStepAsideReason.EnteredMyself, label: 'I entered it myself' },
    { value: ProcessorFeeStepAsideReason.NoFeeApplies, label: 'No fee applies here' },
    { value: ProcessorFeeStepAsideReason.AddLater, label: addLaterLabel },
  ];

  const confirm = () => {
    if (!reason) return;
    const trimmed = note.trim();
    onConfirm(reason, trimmed === '' ? undefined : trimmed);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="fee-row-step-aside"
          type="button"
          size="sm"
          variant="ghost"
          disabled={isPending}
          // Legible against the amber pin; a ghost weight keeps Record the primary action.
          className="text-amber-900 hover:bg-amber-200/70"
        >
          I'll handle it myself
        </Button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="fee-row-step-aside-picker"
        align="end"
        className="w-72 space-y-3 text-left text-sm text-foreground"
        // Portal content bubbles clicks through the REACT tree, not the DOM tree, so a click
        // inside here would otherwise reach any ancestor row/card click handler in the ledger.
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-medium">How do you want to handle this fee?</p>
        <fieldset
          className="space-y-2"
          role="radiogroup"
          aria-label="How you'll handle the processor fee"
        >
          {reasons.map((r) => (
            <label
              key={r.value}
              data-testid={`fee-row-step-aside-reason-${r.value}`}
              className="flex cursor-pointer items-start gap-2"
            >
              <input
                type="radio"
                name="fee-step-aside-reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
                className="mt-1 h-4 w-4 shrink-0"
              />
              <span>{r.label}</span>
            </label>
          ))}
        </fieldset>
        <textarea
          data-testid="fee-row-step-aside-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Add a note (optional)"
          rows={2}
          className="w-full rounded-md border border-input bg-background p-2 text-sm"
        />
        <div className="flex items-center justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            data-testid="fee-row-step-aside-confirm"
            type="button"
            size="sm"
            disabled={!reason || isPending}
            onClick={confirm}
          >
            Step aside
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
